import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { RepeatFormInput } from 'shared/components/ui/habit-form/repeat-form-input';
import { Icon } from 'shared/components/ui/icon';
import { TaskCreateLinkedItemSelect } from 'shared/components/ui/task-create-linked-item-select';
import { createDefaultHabitSchedule } from 'shared/constants';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { PageEvents } from 'shared/services/tracking';
import { Goal } from 'shared/types/goal';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import {
  OnboardingGoal,
  OnboardingLocalizedLifeArea,
} from 'shared/types/onboarding-answers';
import { MinimalNewTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { WeekDays } from 'shared/types/week-days';

import { OnboardingHeader } from '../onboarding-header';
import { OnboardingSideImage } from '../onboarding-side-image';
import { ProgressBar } from '../progress-bar';
import * as Styled from './habits.style';

const HABITS_IMAGE_URL = '/images/onboarding/habit-tracker.png';

const noop = () => {};

type LinkedItem = {
  lifeAreaId: ID | null;
  goalId: ID | null;
};

export type HabitsProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  weekStartsOn: WeekDays;
  lifeAreas: OnboardingLocalizedLifeArea[];
  goals: OnboardingGoal[];
  onSubmit: (habits: MinimalNewTask[]) => void;
};

export const Habits: React.FC<HabitsProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  weekStartsOn,
  lifeAreas,
  goals,
  onSubmit,
}) => {
  useTrackPageVisitEvent(PageEvents.OnboardingHabits, undefined);
  const { t } = useTranslation();
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const [habits, setHabits] = useState(['', '']);
  const [habitSchedules, setHabitSchedules] = useState<HabitSchedule[][]>([
    [createDefaultHabitSchedule()],
    [createDefaultHabitSchedule()],
  ]);
  const [habitTypes, setHabitTypes] = useState<TaskType[]>([
    TaskType.Habit,
    TaskType.Habit,
  ]);
  const [selectedLinkedItems, setSelectedLinkedItems] = useState<
    (LinkedItem | undefined)[]
  >([undefined, undefined]);

  const suggestions = useMemo(
    () => [
      t('forms.onboarding.habits.suggestions.1'),
      t('forms.onboarding.habits.suggestions.2'),
      t('forms.onboarding.habits.suggestions.3'),
      t('forms.onboarding.habits.suggestions.4'),
      t('forms.onboarding.habits.suggestions.5'),
      t('forms.onboarding.habits.suggestions.6'),
      t('forms.onboarding.habits.suggestions.7'),
    ],
    [t],
  );

  const unusedSuggestions = useMemo(
    () => suggestions.filter((s) => !habits.includes(s)),
    [suggestions, habits],
  );

  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window;
    if (firstInputRef.current && !isTouchDevice) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleHabitChange = (index: number, value: string) => {
    const newHabits = [...habits];
    newHabits[index] = value;
    setHabits(newHabits);
  };

  const handleSuggestionClick = (habitText: string) => {
    const firstEmptyIndex = habits.findIndex((habit) => !habit);
    if (firstEmptyIndex !== -1) {
      handleHabitChange(firstEmptyIndex, habitText);
    }
  };

  const handleLinkedItemChange = (index: number, linkedItem: LinkedItem) => {
    const newLinkedItems = [...selectedLinkedItems];
    newLinkedItems[index] = linkedItem;
    setSelectedLinkedItems(newLinkedItems);
  };

  const handleScheduleChange = (
    index: number,
    schedules: HabitSchedule[] | null,
  ) => {
    const newSchedules = [...habitSchedules];
    newSchedules[index] = schedules || [createDefaultHabitSchedule()];
    setHabitSchedules(newSchedules);
  };

  const handleTypeChange = (index: number, type: TaskType) => {
    const newTypes = [...habitTypes];
    newTypes[index] = type;
    setHabitTypes(newTypes);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(
      habits
        .map((habit, index) => ({
          name: habit,
          schedules: habitSchedules[index],
          type: habitTypes[index] as TaskType.Habit | TaskType.Repeating,
          ...(selectedLinkedItems[index] ?? {}),
        }))
        .filter(({ name }) => !!name),
    );
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />
        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Form id="habits-form" onSubmit={handleSubmit}>
            <Styled.Content>
              <Typography variant="h4" component="h1">
                {t('forms.onboarding.habits.title')}
              </Typography>
              <Styled.Subtitle>
                {t('forms.onboarding.habits.subtitle')}
              </Styled.Subtitle>
            </Styled.Content>

            <Styled.HabitsContainer>
              {habits.map((habit, index) => (
                <Styled.HabitRow key={index}>
                  <Styled.Input
                    ref={index === 0 ? firstInputRef : null}
                    placeholder={t('forms.onboarding.habits.placeholder')}
                    value={habit}
                    onChange={(e) => handleHabitChange(index, e.target.value)}
                  />

                  <Styled.HabitRowControl $isRepeat>
                    <RepeatFormInput
                      onChange={(schedules) =>
                        handleScheduleChange(index, schedules)
                      }
                      onChangeType={(type) => handleTypeChange(index, type)}
                      value={habitSchedules[index]}
                      weekStartsOn={weekStartsOn}
                      onPremium={noop}
                      targetAllowed
                      createHabitAllowed
                      shouldReplaceSchedule
                      popupOwnWidth
                      isSmall
                      noBorders
                    />
                  </Styled.HabitRowControl>
                  <Styled.HabitRowControl>
                    <TaskCreateLinkedItemSelect
                      onChange={(linkedItem) =>
                        handleLinkedItemChange(index, linkedItem)
                      }
                      lifeAreaId={selectedLinkedItems[index]?.lifeAreaId}
                      goalId={selectedLinkedItems[index]?.goalId}
                      lifeAreas={lifeAreas as LifeArea[]}
                      goals={goals as Goal[]}
                      onboarding
                    />
                  </Styled.HabitRowControl>
                </Styled.HabitRow>
              ))}
            </Styled.HabitsContainer>

            <Styled.ChipsRow>
              {unusedSuggestions.map((habit) => (
                <Styled.Chip
                  key={habit}
                  type="button"
                  onClick={() => handleSuggestionClick(habit)}
                >
                  <Icon icon={Plus} />
                  <Styled.ChipLabel>{habit}</Styled.ChipLabel>
                </Styled.Chip>
              ))}
            </Styled.ChipsRow>
          </Styled.Form>

          <Styled.FooterArea>
            <Button
              type="submit"
              form="habits-form"
              size={Sizes.Medium}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.continue.label')}
            </Button>
            <Button
              type="button"
              onClick={nextStep}
              variant={Variants.Text}
              size={Sizes.Medium}
              color={Colors.Inherit}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.skip.label')}
            </Button>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <OnboardingSideImage imageUrl={HABITS_IMAGE_URL} maxImageWidth={665} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
